import { useState, useEffect } from 'react';
import { getApiUrl, type ActiveMind } from '../services/api';

const POLL_INTERVAL = 30_000;

/**
 * Hannah's private runtime as seen through cortex GET /runtime/status (apps/cortex/src/runtime-gateway.ts, creator-only)
 * → hannah-runtime GET /status (apps/hannah-runtime/src/store.mjs `status()`: lifecycle only, no message text).
 * Only the fields read below are relied on; everything else in that body is ignored here.
 */
export type RuntimeStatusView =
  | { kind: 'loading' }
  /** This cortex has no /runtime/status route (404): the gateway is not deployed there. Shown as nothing. */
  | { kind: 'absent' }
  | { kind: 'unavailable'; detail: string; routing: string | null }
  | {
      kind: 'ok';
      /** X-Hannah-Runtime-Routing: whether her creator chat is actually routed to the runtime. */
      routing: string | null;
      supervisor: string | null;
      containerRunning: boolean | null;
      /** Accepted conversation/notes not yet committed (inbox `pending` + `claimed`). */
      waiting: number;
      /** History writes back to her memory, by status (`recorded`, `retrying`, ...). */
      history: Record<string, number>;
      /** The runtime's configured core model (hannah-runtime worker.ts status() `configuredModel: CORE_MODEL`). */
      configuredModel: string | null;
      /** The model recorded on her most recent committed reply (store.mjs status() `results[0].model`). */
      lastReplyModel: string | null;
      /** Inbox tasks claimed (being worked) and pending (waiting to start). */
      claimed: number;
      queued: number;
      /** Kinds of the unfinished tasks among the 20 most recent (`tasks[].kind`). */
      activeKinds: string[];
      /** Current boot (`boots[0]`): its last reported activity label and heartbeat time. */
      bootActivity: string | null;
      lastHeartbeatAt: string | null;
      /** store.mjs status() `autonomy` (metadata only); `enabled` null when the runtime does not report it. */
      autonomy: {
        enabled: boolean | null;
        nextIntention: { source: string | null; dueAt: string | null } | null;
        episodes: number | null;
        journal: Record<string, number>;
        journalLastError: string | null;
        lastBlockedBy: string | null;
      } | null;
      /** `usage.blocked`, only if the runtime reports it. */
      usageBlocked: string | null;
    };

interface RuntimeStatusBody {
  counts?: Record<string, number>;
  memoryWrites?: Record<string, number>;
  supervisorState?: { state?: string } | null;
  container?: { running?: boolean } | null;
  configuredModel?: unknown;
  results?: Array<{ model?: unknown }>;
  tasks?: Array<{ kind?: unknown; status?: unknown }>;
  boots?: Array<{ activity?: unknown; lastSeenAt?: unknown }>;
  usage?: { blocked?: unknown } | null;
  autonomy?: {
    enabled?: unknown; episodes?: unknown; journal?: Record<string, number>; journalLastError?: unknown; lastBlockedBy?: unknown;
    nextIntention?: { source?: unknown; dueAt?: unknown } | null;
  } | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** The runtime-Doing fields of a status body; absent fields stay null rather than being guessed. */
function doingFields(body: RuntimeStatusBody) {
  const a = body.autonomy;
  const blocked = body.usage?.blocked;
  return {
    claimed: body.counts?.claimed ?? 0,
    queued: body.counts?.pending ?? 0,
    activeKinds: [...new Set((body.tasks ?? []).filter(t => t.status === 'pending' || t.status === 'claimed').map(t => String(t.kind)))],
    bootActivity: str(body.boots?.[0]?.activity),
    lastHeartbeatAt: str(body.boots?.[0]?.lastSeenAt),
    autonomy: a ? {
      enabled: typeof a.enabled === 'boolean' ? a.enabled : null,
      nextIntention: a.nextIntention ? { source: str(a.nextIntention.source), dueAt: str(a.nextIntention.dueAt) } : null,
      episodes: typeof a.episodes === 'number' ? a.episodes : null,
      journal: a.journal ?? {},
      journalLastError: str(a.journalLastError),
      lastBlockedBy: str(a.lastBlockedBy),
    } : null,
    usageBlocked: typeof blocked === 'string' ? blocked : blocked === true ? 'blocked' : null,
  };
}

/** Cortex says her chat goes to the runtime. Unknown (loading/absent/null) is NOT routed. */
export function isRuntimeRouted(view: RuntimeStatusView | null): boolean {
  return !!view && (view.kind === 'ok' || view.kind === 'unavailable') && view.routing === 'enabled';
}

/** Hannah only: Amanda has no runtime and her cortex has no such route, so nothing is requested for her. */
export function useRuntimeStatus(token: string | null, mind: ActiveMind, enabled: boolean): RuntimeStatusView | null {
  const [view, setView] = useState<RuntimeStatusView | null>(null);
  const active = enabled && mind === 'hannah' && !!token;

  useEffect(() => {
    if (!active) { setView(null); return; }
    let cancelled = false;
    setView({ kind: 'loading' });
    const read = async () => {
      let next: RuntimeStatusView;
      try {
        const res = await fetch(`${getApiUrl(mind)}/runtime/status`, { headers: { Authorization: `Bearer ${token}` } });
        const routing = res.headers.get('X-Hannah-Runtime-Routing');
        if (res.status === 404) next = { kind: 'absent' };
        else if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const body = await res.json() as { error?: { message?: string } };
            if (body.error?.message) detail = body.error.message;
          } catch { /* status code is the detail */ }
          next = { kind: 'unavailable', detail, routing };
        } else {
          const body = await res.json() as RuntimeStatusBody;
          next = {
            kind: 'ok',
            routing,
            supervisor: body.supervisorState?.state ?? null,
            containerRunning: typeof body.container?.running === 'boolean' ? body.container.running : null,
            waiting: (body.counts?.pending ?? 0) + (body.counts?.claimed ?? 0),
            history: body.memoryWrites ?? {},
            configuredModel: typeof body.configuredModel === 'string' ? body.configuredModel : null,
            lastReplyModel: typeof body.results?.[0]?.model === 'string' ? body.results[0].model as string : null,
            ...doingFields(body),
          };
        }
      } catch (err) {
        next = { kind: 'unavailable', detail: err instanceof Error ? err.message : String(err), routing: null };
      }
      if (!cancelled) setView(next);
    };
    read();
    const interval = setInterval(read, POLL_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [active, mind, token]);

  return view;
}
